import { r as registerInstance, c as createEvent, h, H as Host } from './index-662ec80e.js';
import { w as waitNextRender, r as resolveNextRender } from './browser-202fc7f4.js';
import { r as runValidation } from './form-4ba95191.js';
import { c as currentLocale } from './i18n-939e2dee.js';
import { p as propCheck, r as runWatchers, h as hasPropError, d as displayPropError } from './propCheck-e7571e3e.js';
import { b as vMinLength, a1 as vUndefOr, a as vString, Q as vBoolean, _ as vNull, J as vNumber, a2 as vFunction } from './validation-02b8d01d.js';

const dsInputRadioCss = "ds-input-radio{position:relative;display:-ms-flexbox;display:flex;-ms-flex-direction:column;flex-direction:column;margin-bottom:calc(var(--gw) * 0.2)}ds-input-radio input{position:absolute;z-index:-1}ds-input-radio label{padding:calc(var(--gw) * 0.8);background:var(--white);cursor:pointer;font-size:0.88888889rem;line-height:1.56rem}ds-input-radio label:hover{background-color:var(--grey-300)}ds-input-radio label .checkmark{position:relative;display:-ms-flexbox;display:flex;-ms-flex-align:center;align-items:center;pointer-events:none}ds-input-radio label .checkmark::before{content:'';border-radius:100%;border:1px solid var(--grey-700);display:inline-block;width:1rem;height:1rem;top:-0.2rem;margin-right:calc(var(--gw) * 0.75);vertical-align:top;cursor:pointer;text-align:center;transition:all 250ms ease}ds-input-radio .help-error,ds-input-radio .help-info{display:block;padding:0.2rem 1rem 0;line-height:1;font-size:0.79012346rem;line-height:1.3rem;color:var(--grey-900)}ds-input-radio.error label,ds-input-radio.error .help-error{display:block;color:var(--red-500) !important}ds-input-radio[is-checked] label{background-color:var(--grey-300)}ds-input-radio[is-checked] .checkmark::before{background-color:var(--primary);box-shadow:inset 0 0 0 3px #fff}ds-input-radio[is-checked] .checkmark::after{transform:scale(0.4);transition:transform 0.3s ease}ds-input-radio[is-disabled] .checkmark::before{background-color:var(--grey-500);border:1px solid var(--grey-500)}ds-input-radio[is-disabled] label{cursor:not-allowed;color:var(--coolGrey-500)}ds-input-radio[is-readonly] label{cursor:default}";

const DsInputradio = class {
  constructor(hostRef) {
    registerInstance(this, hostRef);
    this.dsInput = createEvent(this, "dsInput", 7);
    this.nextRenderPromises = [];
    this.displayError = false;
    this.validationError = undefined;
    this.injectedError = undefined;
    this.propError = {};
    this.currentLocale = currentLocale();
    this.name = undefined;
    this.label = undefined;
    this.helperText = undefined;
    this.isDisabled = false;
    this.isReadonly = false;
    this.value = null;
    this.checkedValue = undefined;
    this.validate = undefined;
    this.isChecked = undefined;
  }
  // istanbul ignore next
  updateLocale() {
    this.currentLocale = currentLocale();
  }
  watchName() {
    /* istanbul ignore if */
    if (!propCheck(this, 'name', 'string', vMinLength(1)))
      return;
  }
  watchLabel() {
    /* istanbul ignore if */
    if (!propCheck(this, 'label', 'string', vMinLength(1)))
      return;
  }
  watchHelperText() {
    /* istanbul ignore if */
    if (!propCheck(this, 'helperText', 'string', vUndefOr(vString)))
      return;
  }
  async watchIsDisabled() {
    /* istanbul ignore if */
    if (!propCheck(this, 'isDisabled', 'boolean', vBoolean))
      return;
    if (this.isDisabled && this.isChecked)
      this.dsInput.emit(null);
    await waitNextRender(this);
    runValidation(this);
  }
  async watchIsReadonly() {
    /* istanbul ignore if */
    if (!propCheck(this, 'isReadonly', 'boolean', vBoolean))
      return;
    await waitNextRender(this);
    runValidation(this);
  }
  watchValue() {
    /* istanbul ignore if */
    if (!propCheck(this, 'value', 'null | string | number', v => vNull(v) && vString(v) && vNumber(v)))
      return;
    this.setCheckedFromValue();
    this.injectedError = undefined;
    runValidation(this);
  }
  watchCheckedValue(_, oldCheckedValue) {
    /* istanbul ignore if */
    if (!propCheck(this, 'checkedValue', 'string | number', v => vMinLength(1)(v) && vNumber(v)))
      return;
    if (this.value === oldCheckedValue) {
      this.dsInput.emit(this.checkedValue);
    }
  }
  watchValidate() {
    /* istanbul ignore if */
    if (!propCheck(this, 'validate', '(value: any) => string | undefined', vUndefOr(vFunction)))
      return;
    runValidation(this);
  }
  async injectError(error) {
    this.injectedError = error;
    runValidation(this);
    this.displayError = true;
  }
  async hideError() {
    this.injectedError = undefined;
    runValidation(this);
    this.displayError = false;
  }
  setCheckedFromValue() {
    this.isChecked = this.value === this.checkedValue;
  }
  connectedCallback() {
    runWatchers(this);
    this.updateLocale();
  }
  componentDidLoad() {
    if (!this.inputElement)
      return;
    this.dsForm = this.inputElement.closest('ds-form');
    this.setCheckedFromValue();
    runValidation(this);
  }
  componentDidRender() {
    resolveNextRender(this);
  }
  disconnectedCallback() {
    if (this.dsForm)
      this.dsForm.checkValidity();
  }
  render() {
    if (hasPropError(this))
      return displayPropError(this);
    const error = this.displayError && (this.injectedError || this.validationError);
    return (h(Host, { class: { error: !!error }, "is-checked": this.isChecked }, h("label", null, h("input", { ref: el => this.inputElement = el, name: this.name, id: `ds-input-${this.name}-${this.checkedValue}`, type: "radio", disabled: this.isDisabled || this.isReadonly, checked: this.isChecked, value: this.checkedValue, onInput: () => {
        this.displayError = true;
        /* istanbul ignore else */
        if (this.inputElement.checked) {
          this.isChecked = true;
          this.dsInput.emit(this.checkedValue);
        }
      }, onBlur: () => this.displayError = true, onInvalid: () => this.displayError = true }), h("span", { class: 'checkmark' }, this.label)), h("div", { class: "help-info" }, this.helperText, h("slot", { name: "helperText" })), error && h("div", { class: "help-error" }, error)));
  }
  static get watchers() { return {
    "name": ["watchName"],
    "label": ["watchLabel"],
    "helperText": ["watchHelperText"],
    "isDisabled": ["watchIsDisabled"],
    "isReadonly": ["watchIsReadonly"],
    "value": ["watchValue"],
    "checkedValue": ["watchCheckedValue"],
    "validate": ["watchValidate"]
  }; }
};
DsInputradio.style = dsInputRadioCss;

export { DsInputradio as ds_input_radio };
